import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import esLocale from 'date-fns/locale/es';
import { useFirestore } from 'react-redux-firebase';
import moment from 'moment';
// import ExportExcel from 'react-export-excel';
import { useSelector } from 'react-redux';
import Info from './components/Info';
import Export from './components/Export';
import SendTemplates from './SendTemplates/SendTemplates';

import { getDayRange, getMonthName, keywordType } from '../../utils/utils';

const useStyles = makeStyles((theme) => ({
  body: {
    maxWidth: '100%',
    overflowX: 'auto',
  },
  bodyReference: {
    paddingBlock: 8,
  },
  status: {
    color: theme.palette.white.main,
    borderRadius: 50,
    fontSize: 12,
    padding: '2px 12px',
  },
  statusDelivered: {
    backgroundColor: '#65BD6B',
  },
  statusPending: {
    backgroundColor: '#FFBC3A',
  },
  statusRefused: {
    backgroundColor: '#FD7667',
  },
  filterItem: {
    display: 'flex',
    alignItems: 'flex-end',
    paddingBottom: 8,
  },
  copyTag: {
    display: 'flex',
    width: 'max-content',
    cursor: 'pointer',
    borderRadius: 4,
    paddingBlock: 0,
    paddingInline: 16,
    backgroundColor: '#9C27B0',
    color: theme.palette.white.main,
    '&:hover': {
      backgroundColor: '#b834cf',
    },
    marginLeft: 'auto',
  },
  reference: {
    fontSize: theme.sizes.sm.t1,
    minWidth: '85px',
  },
}));

export default function Notifications() {
  const classes = useStyles();
  const small = useMediaQuery('(max-width:600px)');
  const organization = useSelector((state) => state.app.organization);
  const firestore = useFirestore();
  const [notifications, setNotifications] = useState([]);
  const [date, setDate] = useState(moment());
  const [page, setPage] = useState(1);
  const myRef = useRef(null);
  const projectId = process.env.REACT_APP_PROJECT_ID;

  const [search, setSearch] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const profile = useSelector((state) => state.firebase.profile);

  const [initial] = getDayRange(date);
  const [, end] = getDayRange(date);

  useEffect(() => {
    if (page !== 1) setPage(1);
  }, [search]);

  useEffect(() => {
    setSelectedTemplate('');
  }, [organization.id]);

  useEffect(() => {
    if (organization && organization.id) {
      let promise = firestore.collection('gestiones').where('type', '==', 'notificacion');
      const type = keywordType(search[0] === '0' ? search.slice(1) : search);
      if (search && type === 'phone') {
        promise = promise.where('templateParams.phone', '==', search);
      } else if (search) {
        const filter = search[0] === '0' ? search : '0' + search;
        promise = promise.where('templateParams.patientDNI', '==', `D.N.I. ${filter}`);
      } else {
        promise = promise
          .where('date', '>', initial.toDate())
          .where('date', '<', end.toDate())
          .limit(100);
      }
      promise
        .where('organizationId', '==', organization.id)
        .orderBy('date', 'desc')
        .get()
        .then((res) => {
          let _notifications = [];
          res.forEach((e) => _notifications.push({ id: e.id, ...e.data() }));
          _notifications = _notifications.map((e) => {
            let phone = '';
            if (e.sessionId) {
              phone = e.sessionId;
              while (phone.indexOf('-') !== -1) {
                phone = phone.slice(phone.indexOf('-') + 1);
              }
            }
            phone = phone.replace('+', '');
            return {
              ...e,
              phone,
            };
          });
          setNotifications(_notifications.filter((e) => e.status));
        });
    }
  }, [date, search, firestore, organization, organization.id]);

  const getTemplateName = (notification) => {
    if (notification.templateParams?.templateName) {
      return notification.templateParams.templateName;
    }
    if (notification.templateName) {
      return notification.templateName;
    }
    return null;
  };

  const uniqueTemplateNames = useMemo(() => {
    return [
      ...new Set(
        notifications
          .map((notification) => getTemplateName(notification))
          .filter((name) => name && name.trim() !== ''),
      ),
    ].sort();
  }, [notifications]);

  const filteredNotifications = useMemo(() => {
    if (!selectedTemplate) {
      return notifications;
    }
    return notifications.filter((notification) => {
      const templateName = getTemplateName(notification);
      return templateName === selectedTemplate;
    });
  }, [notifications, selectedTemplate]);

  useEffect(() => {
    setPage(1);
  }, [selectedTemplate]);

  const deliveredMessages = useMemo(() => {
    return filteredNotifications.filter((e) => e.status === 'delivered' || e.status === 'read')
      .length;
  }, [filteredNotifications]);

  const pendingMessages = useMemo(() => {
    return filteredNotifications.filter((e) => e.status === 'sent' || e.status === 'enqueued')
      .length;
  }, [filteredNotifications]);

  const failedMessages = useMemo(() => {
    return filteredNotifications.filter((e) => e.status === 'failed').length;
  }, [filteredNotifications]);

  const getNotificationName = (notification) => {
    if (organization && organization.templates) {
      const template = organization.templates.find(
        (e) => e.id === notification.templateId || e.id === notification.templateParams.templateId,
      );
      if (template) {
        return template.displayName;
      }
    }
  };

  const handleOpenGestion = (id) => {
    window.open(
      `https://console.firebase.google.com/u/0/project/${projectId}/firestore/data/~2Fgestiones/${id}`,
    );
  };

  const monthName = getMonthName(moment().get('month'));

  const pages = useMemo(() => {
    return filteredNotifications.length % 20 === 0
      ? parseInt(filteredNotifications.length / 20, 10)
      : parseInt(filteredNotifications.length / 20, 10) + 1;
  }, [filteredNotifications]);

  const formatDNI = (DNI) => DNI && DNI.replace(/\D/g, '');
  const renderDNIBlock = (DNI) => (
    <Box my={1} ml={1}>
      <Typography sx={{ display: 'flex' }}>
        <span className={classes.reference}>DNI:</span> {formatDNI(DNI)}
      </Typography>
    </Box>
  );

  return (
    <Box pb="64px" sx={{ px: small ? 4 : 0 }}>
      <SendTemplates />
      <Box mb={2} className={classes.reference}>
        <Typography sx={{ paddingBlock: 2 }}>Datos de {monthName}</Typography>
      </Box>
      <Info
        notifications={notifications}
        deliveredMessages={deliveredMessages}
        pendingMessages={pendingMessages}
        failedMessages={failedMessages}
      />
      <Export />
      <Box mt={2} ref={myRef}>
        <Box my={2} className={classes.reference}>
          <Typography>Últimos 100 del día</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <LocalizationProvider adapterLocale={esLocale} dateAdapter={AdapterDateFns}>
              <Box display="flex" flexDirection="row">
                <DesktopDatePicker
                  label="Fecha"
                  inputFormat="dd/MM/yyyy"
                  value={date}
                  onChange={(date) => setDate(moment(new Date(date)))}
                  renderInput={(params) => <TextField {...params} />}
                />
              </Box>
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={4} className={classes.filterItem} sx={{ my: small ? 2 : 0 }}>
            <FormControl fullWidth className={classes.formControl}>
              <TextField
                id="standard-basic"
                label="Buscar por número de destinatario"
                name="typeValue"
                variant="outlined"
                autoComplete="cc-csc"
                onChange={(event) => setSearch(event.target.value)}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} className={classes.filterItem} sx={{ my: small ? 2 : 0 }}>
            <FormControl fullWidth className={classes.formControl}>
              <InputLabel id="template-filter-label">Filtrar por notificación</InputLabel>
              <Select
                labelId="template-filter-label"
                id="template-filter"
                value={selectedTemplate}
                label="Filtrar por notificación"
                onChange={(event) => setSelectedTemplate(event.target.value)}
              >
                <MenuItem value="">
                  <em>Todos</em>
                </MenuItem>
                {uniqueTemplateNames.map((templateName) => (
                  <MenuItem key={templateName} value={templateName}>
                    {templateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container className={classes.body}>
          {(!filteredNotifications || filteredNotifications.length === 0) && (
            <Box my={2}>
              <Typography>Aún no tiene notificaciones para el día seleccionado</Typography>
            </Box>
          )}
          {pages > 1 && filteredNotifications.length > 0 ? (
            <Box display="flex" justifyContent="center" my={2} width="100%">
              <Pagination
                siblingCount={0}
                count={pages}
                color="primary"
                page={page}
                onChange={(e, value) => {
                  setPage(value);
                  myRef.current.scrollIntoView();
                }}
              />
            </Box>
          ) : null}
          {filteredNotifications
            ?.slice((page - 1) * 20, page * 20)
            .sort((notifA, notifB) => {
              const dateA = moment(new Date(notifA.date.seconds * 1000));
              const dateB = moment(new Date(notifB.date.seconds * 1000));
              return dateA < dateB ? 1 : -1;
            })
            .map((notification) => (
              <Box key={notification.id} my={2} sx={{ width: small ? '100%' : '40%', mx: 'auto' }}>
                <Paper>
                  <Box display="flex" p={2}>
                    <Box
                      flex={1}
                      display="flex"
                      flexDirection="column"
                      className={classes.bodyReference}
                    >
                      <Box my={1} ml={1}>
                        <Typography sx={{ display: 'flex' }}>
                          <span className={classes.reference}>Fecha de envío:</span>{' '}
                          {moment(new Date(notification.date.seconds * 1000)).format(
                            'DD/MM/YY HH:mm',
                          )}
                        </Typography>
                      </Box>
                      {notification.templateParams.msgId && (
                        <Box my={1} ml={1}>
                          <Typography sx={{ display: 'flex' }}>
                            <span className={classes.reference}>ID Turno:</span>{' '}
                            {notification.templateParams.msgId}
                          </Typography>
                        </Box>
                      )}
                      <Box my={1} ml={1}>
                        <Typography sx={{ display: 'flex' }}>
                          <span className={classes.reference}>Telefono:</span>{' '}
                          {notification &&
                            notification.templateParams &&
                            notification.templateParams.phone}
                        </Typography>
                      </Box>
                      {renderDNIBlock(
                        (notification &&
                          notification.templateParams &&
                          notification.templateParams.patientDNI) ||
                          (notification.patientInfo && notification.patientInfo.DNI),
                      )}
                      {notification &&
                        notification.templateParams &&
                        notification.templateParams.serviceName && (
                          <Box my={1} ml={1}>
                            <Typography sx={{ display: 'flex' }}>
                              <span className={classes.reference}>Servicio:</span>{' '}
                              {notification.templateParams &&
                                notification.templateParams.serviceName}
                            </Typography>
                          </Box>
                        )}
                      <Box my={1} ml={1}>
                        <Typography sx={{ display: 'flex' }}>
                          <span className={classes.reference}>Tipo:</span>{' '}
                          {getNotificationName(notification)}
                        </Typography>
                      </Box>
                      <Box my={1} ml={1}>
                        <Typography sx={{ display: 'flex' }}>
                          <span className={classes.reference}>Es reenvío:</span>{' '}
                          {notification.isSecondDelivery ? 'Sí' : 'No'}
                        </Typography>
                      </Box>
                      {notification.status === 'failed' && notification.reason && (
                        <Box my={1} ml={1}>
                          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                            <span className={classes.reference}>Razon de error:</span>{' '}
                            {notification.reason}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="flex-end">
                      <Box my="4px">
                        {notification.notificationStatus &&
                          notification.notificationStatus.answered &&
                          notification.notificationStatus.answer && (
                            <Typography className={`${classes.status} ${classes.statusDelivered}`}>
                              confirmado
                            </Typography>
                          )}
                        {notification.notificationStatus &&
                          notification.notificationStatus.answered &&
                          !notification.notificationStatus.answer && (
                            <Typography className={`${classes.status} ${classes.statusRefused}`}>
                              cancelado
                            </Typography>
                          )}
                      </Box>
                      {notification &&
                        ((notification.templateParams && notification.templateParams.patientDNI) ||
                          (notification.patientInfo && notification.patientInfo.DNI)) &&
                        organization.id !== 'yvFOV3r2X73cffXra4sH' && (
                          <Box my="4px">
                            {notification.notificationStatus &&
                              notification.notificationStatus.answered && (
                                <Typography
                                  className={`${classes.status} ${classes.statusDelivered}`}
                                >
                                  respondido
                                </Typography>
                              )}
                            {notification.notificationStatus &&
                              !notification.notificationStatus.answered && (
                                <Typography
                                  className={`${classes.status} ${classes.statusRefused}`}
                                >
                                  no respondido
                                </Typography>
                              )}
                          </Box>
                        )}
                      <Box my="4px">
                        {(notification.status === 'read' ||
                          notification.status === 'delivered') && (
                          <Typography className={`${classes.status} ${classes.statusDelivered}`}>
                            {notification.status === 'read' ? 'leído' : 'entregado'}
                          </Typography>
                        )}
                        {(notification.status === 'sent' || notification.status === 'enqueued') && (
                          <Typography className={`${classes.status} ${classes.statusPending}`}>
                            pendiente
                          </Typography>
                        )}
                        {notification.status === 'failed' && (
                          <Typography className={`${classes.status} ${classes.statusRefused}`}>
                            rechazado
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ mt: 'auto' }}>
                        {profile && profile.claims && profile.claims.includes('admin') && (
                          <Box
                            onClick={() => handleOpenGestion(notification.id)}
                            className={classes.copyTag}
                          >
                            <Typography sx={{ fontSize: 14 }}>Abrir gestion</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            ))}
          {pages > 1 && filteredNotifications.length > 0 ? (
            <Box display="flex" justifyContent="center" my={2} width="100%">
              <Pagination
                siblingCount={0}
                count={pages}
                color="primary"
                page={page}
                onChange={(e, value) => {
                  setPage(value);
                  myRef.current.scrollIntoView();
                }}
              />
            </Box>
          ) : null}
        </Grid>
      </Box>
    </Box>
  );
}
